'use client';

import { RemoveFormatting } from 'lucide-react';
import { forwardRef, useCallback } from 'react';
import type { ButtonProps } from '@/components/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-ui-primitive/button';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

export interface ClearFormattingButtonProps extends Omit<ButtonProps, 'type'> {
	editor?: ReturnType<typeof useTiptapEditor>['editor'];
}

/**
 * Button that strips all inline marks and resets block nodes to paragraphs.
 * Calls editor.chain().focus().unsetAllMarks().clearNodes().run()
 */
export const ClearFormattingButton = forwardRef<HTMLButtonElement, ClearFormattingButtonProps>(
	({ editor: providedEditor, onClick, children, ...buttonProps }, ref) => {
		const { editor } = useTiptapEditor(providedEditor);

		const handleClick = useCallback(
			(event: React.MouseEvent<HTMLButtonElement>) => {
				onClick?.(event);
				if (event.defaultPrevented) return;
				editor?.chain().focus().unsetAllMarks().clearNodes().run();
			},
			[editor, onClick]
		);

		if (!editor) return null;

		return (
			<Button
				type="button"
				variant="ghost"
				role="button"
				tabIndex={-1}
				aria-label="Clear formatting"
				tooltip="Clear formatting"
				onClick={handleClick}
				{...buttonProps}
				ref={ref}
			>
				{children ?? <RemoveFormatting className="tiptap-button-icon" />}
			</Button>
		);
	}
);

ClearFormattingButton.displayName = 'ClearFormattingButton';
